import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import {
  Button,
  DataTable,
  ErrorState,
  Input,
  ItemCell,
  LoadingState,
  SegmentedControl,
  TableEmpty,
  TablePagination,
  type Column,
} from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { SellerListItem } from "@/lib/api/types";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { useHiddenColumns } from "@/shared/layout/table-columns";
import { useTableDensity } from "@/shared/layout/table-density";
import { useSellersQuery } from "../services/useSellersQuery";
import { SellerDetailDrawer } from "../components/SellerDetailDrawer";
import { SellerCreateDrawer, type CreateMode } from "../components/SellerCreateDrawer";
import { ColumnsMenu, SellerAvatar, StoreKindTag } from "../components/ui";
import "../sellers.css";

type SellersPageProps = {
  locale: Locale;
  limit: number;
  offset: number;
  /**
   * Open the create drawer over the list. Set by the `sellers/new` and
   * `restaurants/new` routes; closing the drawer navigates back.
   */
  create?: CreateMode | null;
};

export function SellersPage({ locale, limit, offset, create = null }: SellersPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const [openSellerId, setOpenSellerId] = useState<string | null>(null);
  const [compact, , setCompact] = useTableDensity();
  const [hiddenColumns, toggleColumn] = useHiddenColumns("sellers");
  const [queryInput, setQueryInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounced, because every keystroke is a request otherwise.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(queryInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [queryInput]);
  const { data, isLoading, error, refetch } = useSellersQuery({
    limit,
    offset,
    q: search || undefined,
  });

  const listPath = (nextLimit: number, nextOffset: number) =>
    `/${locale}/admin/sellers?limit=${nextLimit}&offset=${nextOffset}`;

  const closeCreate = () =>
    navigate(
      create === "restaurant" ? `/${locale}/admin/restaurants` : listPath(limit, offset),
    );

  const columns: Column<SellerListItem>[] = [
    {
      key: "seller",
      label: t(keys.common.name),
      width: 2.2,
      render: (seller) => (
        <ItemCell
          media={
            <SellerAvatar
              id={seller.id}
              name={seller.name}
              // A seller's face is its store's logo when it has one.
              logoUrl={seller.stores.find((store) => store.logoUrl)?.logoUrl}
            />
          }
          name={seller.name}
          // An address is a Latin string whatever the page's language.
          meta={<bdi dir="ltr">{seller.email ?? seller.phone}</bdi>}
        />
      ),
    },
    {
      key: "phone",
      label: t(keys.common.phone),
      width: 1.2,
      render: (seller) => (
        // The cell keeps the page direction (it sits under its header); only
        // the number is an LTR isolate, so its "+" stays in front.
        <span className="rd-num" style={{ color: "var(--rd-text-secondary)" }}>
          <bdi dir="ltr">{seller.phone}</bdi>
        </span>
      ),
    },
    {
      key: "stores",
      label: t(keys.admin.storesTitle),
      width: 2,
      render: (seller) => {
        if (seller.stores.length === 0) {
          return (
            <span style={{ color: "var(--rd-text-meta)" }}>
              {t("admin.sellerNoStoresYet")}
            </span>
          );
        }
        const restaurants = seller.stores.filter(
          (store) => store.storeKind === "RESTAURANT",
        ).length;
        const online = seller.stores.length - restaurants;
        const counts = [
          online ? `${t(keys.admin.onlineStoresTitle)}: ${online}` : null,
          restaurants ? `${t(keys.admin.restaurantsTitle)}: ${restaurants}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <div className="rd-cell-stack">
            <span dir="auto">
              {seller.stores
                .map((store) => store.storeName)
                .join(locale === "ar" ? "، " : ", ")}
            </span>
            <span className="rd-cell-sub">{counts}</span>
          </div>
        );
      },
    },
    {
      key: "type",
      label: t(keys.common.type),
      width: 1.1,
      render: (seller) => {
        const kinds = [...new Set(seller.stores.map((store) => store.storeKind))];
        return (
          <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
            {kinds.map((kind) => (
              <StoreKindTag key={kind} kind={kind} />
            ))}
          </span>
        );
      },
    },
  ];

  const page = Math.floor(offset / limit) + 1;
  const pageName = t(keys.admin.sellersTitle).toLocaleLowerCase(locale);
  const openCreate = () =>
    navigate(`/${locale}/admin/sellers/new?limit=${limit}&offset=${offset}`);

  return (
    <>
      <PageHeaderActions>
        <Button
          variant="primary"
          size="sm"
          icon={Plus}
          onClick={openCreate}
        >
          {t(keys.admin.sellerCreateAction)}
        </Button>
      </PageHeaderActions>

      <div className="rd-toolbar">
        <Input
          icon={Search}
          size="sm"
          type="search"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          placeholder={t("admin.sellersSearchPlaceholder")}
          aria-label={t("admin.sellersSearchPlaceholder")}
          style={{ width: 300, maxWidth: "100%" }}
        />
        {data ? (
          <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
            {t("admin.sellersCount", { count: data.total })}
          </span>
        ) : null}
        <div style={{ flex: 1 }} />
        <SegmentedControl<"comfortable" | "compact">
          label={t(keys.common.rowDensity)}
          value={compact ? "compact" : "comfortable"}
          onChange={(value) => setCompact(value === "compact")}
          options={[
            { value: "comfortable", label: t(keys.common.densityComfortable) },
            { value: "compact", label: t(keys.common.densityCompact) },
          ]}
        />
        <ColumnsMenu
          columns={[
            { key: "phone", label: t(keys.common.phone) },
            { key: "stores", label: t(keys.admin.storesTitle) },
            { key: "type", label: t(keys.common.type) },
          ]}
          hidden={hiddenColumns}
          onToggle={toggleColumn}
        />
      </div>

      {error && !data ? (
        <ErrorState
          title={t(keys.shell.errorTitle, { page: pageName })}
          sub={t(keys.shell.errorSub)}
          onRetry={() => void refetch()}
          retryLabel={t(keys.shell.retry)}
        />
      ) : isLoading && !data ? (
        <LoadingState
          title={t(keys.shell.loadingTitle, { page: pageName })}
          sub={t(keys.shell.loadingSub)}
          rows={Math.min(limit, 6)}
        />
      ) : (
        <DataTable
          label={t(keys.admin.sellersTitle)}
          className={compact ? "sellers-table-compact" : undefined}
          columns={columns.filter((column) => !hiddenColumns.has(column.key))}
          rows={data?.items ?? []}
          rowKey={(seller) => seller.id}
          minWidth={820}
          // Opens the drawer instead of replacing the screen — the admin is
          // working through the list, and a navigation loses their place in it.
          onRowClick={(seller) => setOpenSellerId(seller.id)}
          empty={
            search ? (
              <TableEmpty
                illustration="admin-no-results"
                title={t("admin.sellersSearchEmptyTitle", { query: search })}
                actions={
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={X}
                    onClick={() => {
                      setQueryInput("");
                      setSearch("");
                    }}
                  >
                    {t(keys.common.clearFilters)}
                  </Button>
                }
              >
                {t("admin.sellersSearchEmptySub")}
              </TableEmpty>
            ) : (
              <TableEmpty
                illustration="admin-empty-table"
                title={t("admin.sellersEmptyTitle")}
                actions={
                  <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
                    {t(keys.admin.sellerCreateAction)}
                  </Button>
                }
              >
                {t("admin.sellersEmptySub")}
              </TableEmpty>
            )
          }
          footer={
            data && data.total > 0 ? (
              <TablePagination
                total={data.total}
                page={page}
                pageSize={limit}
                onPage={(next) => navigate(listPath(limit, (next - 1) * limit))}
                onPageSize={(size) => navigate(listPath(size, 0))}
                locale={locale}
                labels={{
                  range: (from, to, total) =>
                    t(keys.common.paginationSummary, { from, to, total }),
                  rows: t("admin.tableRowsLabel"),
                  rowsPerPage: t("admin.tableRowsPerPage"),
                  previous: t(keys.common.previous),
                  next: t(keys.common.next),
                  pages: t(keys.common.paginationNav),
                }}
              />
            ) : null
          }
        />
      )}

      <SellerDetailDrawer
        seller={data?.items.find((seller) => seller.id === openSellerId) ?? null}
        locale={locale}
        onClose={() => setOpenSellerId(null)}
        onViewSeller={(id) => navigate(`/${locale}/admin/sellers/${id}`)}
      />

      {create ? (
        <SellerCreateDrawer
          key={create}
          mode={create}
          open
          locale={locale}
          onClose={closeCreate}
        />
      ) : null}
    </>
  );
}
